import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, Button, Input, VStack, Heading, useToast, Flex, 
  Text, Icon, useColorModeValue, InputGroup, InputLeftElement, Select
} from '@chakra-ui/react';
import { MdAddAlert, MdVideoCall, MdMenuBook } from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

export default function MeetingManager() {
  const [roomName, setRoomName] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdMeetingInfo, setCreatedMeetingInfo] = useState(null);
  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const headingColor = useColorModeValue("gray.700", "white");
  const textColor = useColorModeValue("gray.500", "gray.400");
  const inputBg = useColorModeValue("gray.50", "gray.900");
  const { user } = useContext(AuthContext);

  const handleCreateMeeting = async () => {
    if (!roomName.trim()) {
      toast({ title: "الرجاء إدخال اسم الحصة", status: "warning", duration: 3000, position: "top" });
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/meetings/create`, { roomName });
      if (response.data.success) {
        toast({
          title: "تم إنشاء الحصة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setCreatedMeetingInfo(response.data.data);
        setRoomName("");
      }
    } catch (err) {
      toast({
        title: "خطأ في إنشاء الحصة",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      p={8} 
      shadow="xl" 
      borderWidth="1px" 
      borderColor={borderColor}
      borderRadius="2xl" 
      bg={cardBg} 
      width="100%"
      position="relative"
      overflow="hidden"
    >
      <Box 
        position="absolute" 
        top="-20px" 
        right="-20px" 
        w="100px" 
        h="100px" 
        bg="blue.500" 
        opacity="0.1" 
        borderRadius="full" 
        filter="blur(20px)"
      />
      
      <VStack spacing={5} align="stretch" position="relative" zIndex={1}>
        <Flex align="center" gap={3} dir="rtl">
          <Box p={2} bg="blue.50" color="blue.500" borderRadius="lg">
            <Icon as={MdVideoCall} w={6} h={6} />
          </Box>
          <Heading size="md" color={headingColor}>
            إنشاء حصة جديدة
          </Heading>
        </Flex>
        
        <Text color={textColor} fontSize="sm" dir="rtl">
          قم بإنشاء غرفة جديدة للطلاب للانضمام إليها. سيتم عرضها مباشرة في قائمة الحصص النشطة.
        </Text>

        {createdMeetingInfo ? (
          <Box p={6} bg="green.50" borderRadius="xl" borderWidth="1px" borderColor="green.200" dir="rtl">
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="green.700" textAlign="center">
                تم إنشاء الحصة بنجاح!
              </Heading>
              <Text color="green.600" textAlign="center">
                يرجى مشاركة هذه المعلومات مع الطلاب للانضمام للحصة:
              </Text>
              
              <Flex gap={4} direction={{ base: "column", md: "row" }}>
                <Box flex={1} bg="white" p={4} borderRadius="lg" shadow="sm" borderWidth="1px">
                  <Text fontSize="sm" color="gray.500" mb={1}>رقم الحصة (Meeting ID)</Text>
                  <Text fontSize="xl" fontWeight="bold" letterSpacing="widest" color="blue.600">
                    {createdMeetingInfo.meetingId}
                  </Text>
                </Box>
                <Box flex={1} bg="white" p={4} borderRadius="lg" shadow="sm" borderWidth="1px">
                  <Text fontSize="sm" color="gray.500" mb={1}>الرقم السري (Secret Key)</Text>
                  <Text fontSize="xl" fontWeight="bold" letterSpacing="widest" color="red.500">
                    {createdMeetingInfo.secretKey}
                  </Text>
                </Box>
              </Flex>

              <Button 
                colorScheme="gray" 
                variant="outline"
                onClick={() => setCreatedMeetingInfo(null)}
                mt={2}
              >
                إنشاء حصة أخرى
              </Button>
            </VStack>
          </Box>
        ) : (
          <Flex gap={3} flexDir={{ base: "column", md: "row-reverse" }}>
            <InputGroup size="lg" flex={1}>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdAddAlert} color="gray.600" />
              </InputLeftElement>
              <Input 
                dir="rtl"
                placeholder="اسم الحصة (مثال: مراجعة رياضيات)" 
                value={roomName} 
                onChange={(e) => setRoomName(e.target.value)} 
                borderRadius="xl"
                focusBorderColor="blue.500"
                bg={inputBg}
                border="none"
                _placeholder={{ color: "gray.400" }}
              />
            </InputGroup>

            <Button 
              size="lg"
              colorScheme="blue" 
              onClick={handleCreateMeeting} 
              isLoading={loading}
              borderRadius="xl"
              px={8}
              boxShadow="0 4px 14px 0 rgba(49, 130, 206, 0.39)"
              _hover={{ transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(49, 130, 206, 0.23)" }}
              transition="all 0.2s"
            >
              إنشاء الغرفة
            </Button>
          </Flex>
        )}
      </VStack>
    </Box>
  );
}

